import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

// Cart attribute used to remember an explicit opt-out across page loads
// (cart attributes are stored server-side against the cart cookie, unlike
// localStorage — so this also survives the shopper switching devices while
// signed in). Cleared once the cart is emptied, so a new shopping session
// starts with protection included again.
const REMOVED_ATTRIBUTE_KEY = 'shipping_protection_removed';

/**
 * Silently keeps the shipping-protection line item present in the cart by
 * default, without ever re-adding it once the shopper removes it.
 *
 * There's no dedicated "add"/"remove" UI here — this piggybacks entirely on
 * the existing StandardEvents.cartLinesUpdate bus that cart-icon.js and
 * cart-drawer.js already listen to. Every path that can change the cart
 * (product-form.js, routine-bundle.js, cart-drawer.js's own
 * handleUpsellAdd, and the native quantity-selector remove control) already
 * dispatches through that bus, so listening there — rather than polling or
 * hooking a specific remove button — is sufficient to catch every case.
 *
 * Reconciliation logic, run after every cart mutation settles:
 *   - Cart empty → clear the opt-out flag (fresh session next time).
 *   - Protection line present → nothing to do.
 *   - Protection line missing, mutation was NOT an 'add' → the only way an
 *     unrelated remove/update could make protection disappear is if
 *     protection itself was the line that got removed (removing a
 *     *different* line can't do that) — so record the opt-out and stop.
 *   - Protection line missing, mutation WAS an 'add', opt-out flag not set →
 *     genuinely never added yet (or the very first product just landed in
 *     an empty cart) — add it.
 *   - Protection line missing, mutation WAS an 'add', opt-out flag set →
 *     shopper already removed it this session — respect that, don't re-add.
 *
 * @extends {Component}
 */
class ShippingProtectionAutoAdd extends Component {
  /** @type {string | undefined} */
  get #variantId() {
    return this.dataset.variantId;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const variantId = this.#variantId;
    if (!variantId) return;

    // Ignore our own synthetic add below (its lines are always exactly the
    // protection variant) to avoid re-entering the reconcile loop.
    if (event.action === 'add' && event.lines?.some((line) => line.merchandiseId === variantId)) {
      return;
    }

    event.promise?.then(() => this.#reconcile(event.action, variantId)).catch(() => {});
  };

  /**
   * @param {string} triggeringAction
   * @param {string} variantId
   */
  async #reconcile(triggeringAction, variantId) {
    /** @type {{ item_count: number, items: { variant_id: number }[], attributes: Record<string, string> } | undefined} */
    let cart;
    try {
      cart = await this.#fetchCart();
    } catch {
      return; // Best-effort — a failed check here just skips this cycle.
    }
    if (!cart) return;

    const numericVariantId = Number(variantId);
    const hasProtection = cart.items.some((item) => item.variant_id === numericVariantId);
    const removedFlag = cart.attributes?.[REMOVED_ATTRIBUTE_KEY] === 'true';

    if (cart.item_count === 0) {
      if (removedFlag) await this.#setRemovedFlag(false);
      return;
    }

    if (hasProtection) return;

    if (triggeringAction !== 'add') {
      if (!removedFlag) await this.#setRemovedFlag(true);
      return;
    }

    if (removedFlag) return;

    await this.#addProtection(variantId);
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * @param {boolean} removed
   */
  async #setRemovedFlag(removed) {
    try {
      await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify({ attributes: { [REMOVED_ATTRIBUTE_KEY]: removed ? 'true' : '' } }),
        })
      );
    } catch (error) {
      console.warn('[shipping-protection] Failed to persist opt-out state:', error);
    }
  }

  /**
   * Mirrors cart-drawer.js's handleUpsellAdd: dispatch through the shared
   * event bus (not a bare fetch) so cart-icon.js's bubble count and the open
   * drawer's contents stay in sync via the theme's own render pipeline.
   * @param {string} variantId
   */
  async #addProtection(variantId) {
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }) })
      );
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.message || 'Failed to add shipping protection');
      }

      const cart = await this.#fetchCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false },
      });
    } catch (error) {
      console.warn('[shipping-protection] Auto-add failed:', error);
      deferredEventPromise.reject(error instanceof Error ? error : new Error(String(error)));
    }
  }
}

if (!customElements.get('shipping-protection-auto-add')) {
  customElements.define('shipping-protection-auto-add', ShippingProtectionAutoAdd);
}
